import React, { useMemo, useState, useEffect } from "react";
import {
    ArrowLeft,
    CheckCircle2,
    ExternalLink,
    Loader2,
    RefreshCw,
    Rocket,
    Sparkles,
    Wand2
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import PublicSupportModal from "../components/support/PublicSupportModal";
import {
    enrichGeneratorPayload,
    resolveGeneratorIntentDefaults
} from "../utils/generatorIntentDefaults";

import {
    createAndQueuePreviewSession,
    getPreviewSession,
    getPreviewSessionStatus,
    claimPreviewSession,
    publishProject,
    getPreviewSessionDocumentUrl,
    getActiveGenerationEngine
} from "../services/websiteService";

import {
    getCurrentUser,
    redirectToLogin,
    redirectToSignup
} from "../services/authService";

import "./GenerateWebsitePageV4.css";

const ADMIN_BASE_URL = process.env.REACT_APP_ADMIN_URL || "https://admin.shrianshnexus.in";
const K = "snx_v4_pending_preview_session_id";

const industries = [
    "Real Estate",
    "Healthcare / Clinic",
    "Legal / Law Firm",
    "Academy / Coaching",
    "Fitness",
    "Beauty / Salon",
    "Ecommerce / Store",
    "Restaurant / Cafe",
    "SaaS / Tech"
];

const goals = [
    "Generate Leads",
    "Book Appointment",
    "Book Trial",
    "Sell Products",
    "Get Quote",
    "Collect Enquiries"
];

const styles = ["Premium", "Modern", "Luxury", "Bold", "Clean"];

const steps = [
    "Understanding your business",
    "Selecting V2 blueprint",
    "Planning website pages",
    "Creating CRM modules",
    "Preparing image direction",
    "Building your live preview"
];

const verticalPreviewMap = {
    "Real Estate": {
        key: "real-estate",
        badge: "Real Estate Blueprint",
        title: "Premium Property Hub",
        subtitle: "Search-ready listings, agents and enquiry flow.",
        cta: "Explore Properties",
        accent: "#38bdf8",
        nav: ["Home", "Listings", "Agents", "Areas", "Contact"],
        cards: ["3 BHK Apartment", "Luxury Villa", "Commercial Office"],
        metrics: ["120+ Listings", "Verified Agents", "Lead Forms"]
    },
    "Healthcare / Clinic": {
        key: "healthcare",
        badge: "Healthcare Blueprint",
        title: "PrimeCare Wellness Clinic",
        subtitle: "Doctors, treatments, appointments and trust-first content.",
        cta: "Book Appointment",
        accent: "#22c55e",
        nav: ["Home", "Doctors", "Treatments", "Appointments", "Contact"],
        cards: ["General Care", "Dental Care", "Skin Clinic"],
        metrics: ["Doctors", "Treatments", "Booking"]
    },
    "Legal / Law Firm": {
        key: "legal",
        badge: "Legal Blueprint",
        title: "LexCore Strategic Legal",
        subtitle: "Practice areas, authority content and consultation funnel.",
        cta: "Request Consultation",
        accent: "#fbbf24",
        nav: ["Home", "Practice", "Attorneys", "Cases", "Contact"],
        cards: ["Corporate Law", "Property Law", "Family Law"],
        metrics: ["Practice Areas", "Team", "Consultation"]
    },
    "Academy / Coaching": {
        key: "academy",
        badge: "Academy Blueprint",
        title: "Elite Skills Academy",
        subtitle: "Programs, coaches, admissions and student enquiry flow.",
        cta: "Join Program",
        accent: "#fb923c",
        nav: ["Home", "Programs", "Coaches", "Admissions", "Contact"],
        cards: ["Foundation Program", "Advanced Batch", "Weekend Camp"],
        metrics: ["Programs", "Coaches", "Admissions"]
    },
    Fitness: {
        key: "fitness",
        badge: "Fitness Blueprint",
        title: "Bold Performance Fitness",
        subtitle: "Programs, trainers, memberships and trial booking.",
        cta: "Book Free Trial",
        accent: "#f97316",
        nav: ["Home", "Programs", "Trainers", "Memberships", "Contact"],
        cards: ["Strength", "Fat Loss", "Personal Training"],
        metrics: ["Programs", "Trainers", "Trial"]
    },
    "Beauty / Salon": {
        key: "beauty",
        badge: "Beauty Blueprint",
        title: "Veloura Beauty Studio",
        subtitle: "Services, gallery, specialists and appointment flow.",
        cta: "Book Appointment",
        accent: "#ec4899",
        nav: ["Home", "Services", "Gallery", "Artists", "Contact"],
        cards: ["Hair Styling", "Skin Care", "Bridal Makeup"],
        metrics: ["Services", "Gallery", "Appointments"]
    },
    "Ecommerce / Store": {
        key: "commerce",
        badge: "Commerce Blueprint",
        title: "Aarohi Fine Jewellery",
        subtitle: "Collections, product cards, detail pages and catalogue flow.",
        cta: "Shop Collection",
        accent: "#14b8a6",
        nav: ["Home", "Collections", "Products", "Offers", "Contact"],
        cards: ["Gold Rings", "Diamond Set", "Bridal Jewellery"],
        metrics: ["Collections", "Products", "Catalogue"]
    },
    "Restaurant / Cafe": {
        key: "restaurant",
        badge: "Restaurant Blueprint",
        title: "Harvest Table Kitchen",
        subtitle: "Menu, reservations, ambience and food discovery.",
        cta: "Reserve Table",
        accent: "#84cc16",
        nav: ["Home", "Menu", "Gallery", "Reservations", "Contact"],
        cards: ["Chef Specials", "Family Dining", "Cafe Menu"],
        metrics: ["Menu", "Reservations", "Gallery"]
    },
    "SaaS / Tech": {
        key: "saas",
        badge: "SaaS Blueprint",
        title: "FlowPilot AI",
        subtitle: "Features, benefits, pricing and demo conversion flow.",
        cta: "Book Demo",
        accent: "#8b5cf6",
        nav: ["Home", "Features", "Pricing", "Use Cases", "Contact"],
        cards: ["Automation", "Analytics", "Integrations"],
        metrics: ["Features", "Demo", "Pricing"]
    },
    default: {
        key: "default",
        badge: "Business Blueprint",
        title: "Your Premium Business",
        subtitle: "Website, CRM modules, leads and deployment-ready preview.",
        cta: "Get Started",
        accent: "#22d3ee",
        nav: ["Home", "About", "Services", "Testimonials", "Contact"],
        cards: ["Services", "Team", "Lead Form"],
        metrics: ["Website", "CRM", "Leads"]
    }
};

function q(search, name) {
    return new URLSearchParams(search).get(name) || "";
}

function infer(prompt = "") {
    const text = prompt.toLowerCase();
    if (text.includes("real estate") || text.includes("property")) return "Real Estate";
    if (text.includes("clinic") || text.includes("doctor")) return "Healthcare / Clinic";
    if (text.includes("law") || text.includes("legal")) return "Legal / Law Firm";
    if (text.includes("academy") || text.includes("school")) return "Academy / Coaching";
    if (text.includes("fitness") || text.includes("gym")) return "Fitness";
    if (text.includes("beauty") || text.includes("salon")) return "Beauty / Salon";
    if (text.includes("store") || text.includes("shop") || text.includes("fashion") || text.includes("jewellery")) return "Ecommerce / Store";
    if (text.includes("restaurant") || text.includes("cafe")) return "Restaurant / Cafe";
    if (text.includes("saas") || text.includes("software")) return "SaaS / Tech";
    return "";
}

function buildName(prompt = "") {
    return prompt
        .replace(/^build\s+/i, "")
        .replace(/^create\s+/i, "")
        .split(/\s+/)
        .slice(0, 3)
        .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
        .join(" ");
}

function pid(result) {
    return result?.projectId || result?.claimedProjectId || result?.project?.id || result?.data?.projectId || "";
}

function sid(result) {
    return result?.previewSessionId || result?.id || result?.sessionId || result?.data?.previewSessionId || "";
}

function isReadyStatus(status) {
    return ["Generated", "Completed", "Ready"].includes(status);
}

function isFailedStatus(status) {
    return ["Failed", "Error", "Expired"].includes(status);
}

function DynamicIndustryPreview({ form, screen, stepIndex }) {
    const config = verticalPreviewMap[form.industry] || verticalPreviewMap.default;
    const name = form.businessName?.trim() || config.title;

    const generationItems = [
        "Business detected",
        config.badge,
        "Pages planned",
        "CRM modules ready",
        "Images prepared",
        "Preview assembling"
    ];

    return (
        <div className={`gpv4-live-preview gpv4-live-${config.key}`} style={{ "--v-accent": config.accent }}>
            <div className="gpv4-live-browser-top">
                <span />
                <span />
                <span />
                <strong>{config.badge}</strong>
            </div>

            <div className="gpv4-live-nav">
                <b>{name}</b>
                <div>
                    {config.nav.slice(0, 4).map((x) => (
                        <span key={x}>{x}</span>
                    ))}
                </div>
            </div>

            <div className="gpv4-live-hero">
                <div>
                    <em>{config.badge}</em>
                    <h3>{name}</h3>
                    <p>{form.description?.trim()?.slice(0, 130) || config.subtitle}</p>
                    <button>{config.cta}</button>
                </div>

                <div className="gpv4-live-visual">
                    <i />
                    <i />
                    <i />
                </div>
            </div>

            <div className="gpv4-live-metrics">
                {config.metrics.map((x) => (
                    <span key={x}>
                        <CheckCircle2 size={13} />
                        {x}
                    </span>
                ))}
            </div>

            <div className="gpv4-live-card-grid">
                {config.cards.map((x) => (
                    <div key={x}>
                        <small>{config.key}</small>
                        <strong>{x}</strong>
                        <p>CRM editable section</p>
                    </div>
                ))}
            </div>

            {(screen === "generating" || screen === "setup") && (
                <div className="gpv4-live-overlay">
                    <div className="gpv4-live-pipeline">
                        <Sparkles size={18} />
                        <strong>{screen === "generating" ? steps[stepIndex] : "Ready to build your preview"}</strong>

                        <div>
                            {generationItems.map((x, index) => (
                                <span key={x} className={screen === "generating" && index <= stepIndex ? "done" : ""}>
                                    <CheckCircle2 size={13} />
                                    {x}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function GenerateWebsitePageV4() {
    const loc = useLocation();
    const nav = useNavigate();

    const prompt = loc.state?.premiumPrompt || q(loc.search, "prompt");
    const urlPreviewSessionId = q(loc.search, "previewSessionId");
    const authReturn = q(loc.search, "authReturn") === "1";
    const detectedIndustry = infer(prompt);

    const [form, setForm] = useState({
        businessName: buildName(prompt),
        industry: detectedIndustry,
        description: prompt,
        goalOfWebsite:
            detectedIndustry.includes("Clinic") || detectedIndustry.includes("Beauty")
                ? "Book Appointment"
                : "Generate Leads",
        brandStyle: "Premium",
        location: "",
        primaryAudience: "",
        offerings: ""
    });

    const [screen, setScreen] = useState(urlPreviewSessionId ? "preview" : "setup");
    const [previewSessionId, setPreviewSessionId] = useState(urlPreviewSessionId || "");
    const [projectId, setProjectId] = useState("");
    const [status, setStatus] = useState("");
    const [variant, setVariant] = useState("A");
    const [refreshKey, setRefreshKey] = useState(0);
    const [stepIndex, setStepIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [supportOpen, setSupportOpen] = useState(false);
    const [revealReady, setRevealReady] = useState(false);

    const errors = useMemo(() => {
        const result = [];
        if (!form.businessName.trim()) result.push("Business name is required.");
        if (!form.industry.trim()) result.push("Industry is required.");
        if (!form.description.trim() || form.description.trim().length < 20) result.push("Description should explain your business in at least 20 characters.");
        if (!form.goalOfWebsite.trim()) result.push("Website goal is required.");
        return result;
    }, [form]);

    const doc = useMemo(() => {
        if (!previewSessionId) return "";
        const baseUrl = getPreviewSessionDocumentUrl(previewSessionId);
        const separator = baseUrl.includes("?") ? "&" : "?";
        return `${baseUrl}${separator}v=${variant}&r=${refreshKey || Date.now()}`;
    }, [previewSessionId, variant, refreshKey]);

    function getAuthReturnUrl() {
        const url = new URL(window.location.href);
        const id = previewSessionId || urlPreviewSessionId || localStorage.getItem(K);

        if (id) url.searchParams.set("previewSessionId", id);

        url.searchParams.set("authReturn", "1");
        url.searchParams.set("engine", getActiveGenerationEngine());
        return url.toString();
    }

    useEffect(() => {
        if (screen !== "generating") return;
        const timer = setInterval(() => setStepIndex((current) => (current + 1) % steps.length), 1200);
        return () => clearInterval(timer);
    }, [screen]);

    useEffect(() => {
        if (!authReturn || projectId) return;

        const id = previewSessionId || urlPreviewSessionId || localStorage.getItem(K);
        if (!id) return;

        let cancelled = false;

        async function autoClaimAfterLogin() {
            try {
                setSaving(true);
                setPreviewSessionId(id);
                setScreen("preview");

                const me = await getCurrentUser().catch(() => null);
                if (!me) return;

                const result = await claimPreviewSession(id);
                const claimedProjectId = pid(result);

                if (!claimedProjectId) {
                    toast.error("Could not save preview to CRM.");
                    return;
                }

                if (cancelled) return;

                setProjectId(claimedProjectId);
                setStatus("Saved to CRM");
                localStorage.removeItem(K);
                setRefreshKey(Date.now());

                const cleanUrl = new URL(window.location.href);
                cleanUrl.searchParams.delete("authReturn");
                window.history.replaceState({}, "", cleanUrl.toString());

                toast.success("Preview saved to CRM.");
            } catch (error) {
                console.error(error);
                toast.error("Could not save preview after login.");
            } finally {
                if (!cancelled) setSaving(false);
            }
        }

        autoClaimAfterLogin();

        return () => {
            cancelled = true;
        };
    }, [authReturn, projectId, previewSessionId, urlPreviewSessionId]);

    function update(key, value) {
        setForm((previous) => ({ ...previous, [key]: value }));
    }

    function payload() {
        const defaults = resolveGeneratorIntentDefaults({
            industry: form.industry,
            subIndustry: form.industry,
            description: form.description,
            goalOfWebsite: form.goalOfWebsite,
            callToActionType: form.goalOfWebsite
        });

        return enrichGeneratorPayload({
            businessName: form.businessName,
            industry: form.industry || defaults.industry,
            subIndustry: form.industry || defaults.subIndustry,
            description: form.description,
            tone: "Professional",
            brandStyle: form.brandStyle,
            primaryAudience: form.primaryAudience || "Local customers",
            location: form.location,
            goalOfWebsite: form.goalOfWebsite,
            callToActionType: form.goalOfWebsite,
            coreServices: form.offerings || form.description,
            projectTypes: form.offerings || form.description,
            businessModel: defaults.businessModel,
            primaryWebsiteMode: defaults.primaryWebsiteMode,
            primaryEntityType: defaults.primaryEntityType,
            locationFocus: defaults.locationFocus,
            contentStyleHint: defaults.contentStyleHint,
            pages: defaults.pages || [],
            crmModules: defaults.crmModules || [],
            offeredItems: []
        });
    }

    async function generate() {
        if (errors.length) {
            toast.error(errors[0]);
            return;
        }

        try {
            setScreen("generating");
            setRevealReady(false);
            setStatus("Preparing...");
            setPreviewSessionId("");
            setProjectId("");
            setStepIndex(0);

            const result = await createAndQueuePreviewSession(payload());
            const id = sid(result);

            if (!id) throw new Error("No preview session");

            setPreviewSessionId(id);
            localStorage.setItem(K, id);

            window.history.replaceState(
                {},
                "",
                `/generate?engine=${encodeURIComponent(getActiveGenerationEngine())}&previewSessionId=${encodeURIComponent(id)}`
            );

            const timer = setInterval(async () => {
                try {
                    const currentStatus = (await getPreviewSessionStatus(id))?.status || "";
                    setStatus(currentStatus);

                    if (isReadyStatus(currentStatus)) {
                        clearInterval(timer);
                        await getPreviewSession(id).catch(() => null);

                        setRevealReady(true);
                        setTimeout(() => {
                            setScreen("preview");
                            setRefreshKey(Date.now());
                            toast.success("Premium preview is ready.");
                        }, 900);
                    }

                    if (isFailedStatus(currentStatus)) {
                        clearInterval(timer);
                        setScreen("setup");
                        toast.error("Generation failed.");
                    }
                } catch (error) {
                    clearInterval(timer);
                    console.error(error);
                    setScreen("setup");
                    toast.error("Could not check preview status.");
                }
            }, 2800);
        } catch (error) {
            console.error(error);
            setScreen("setup");
            toast.error("Failed to start premium preview.");
        }
    }

    async function save() {
        try {
            setSaving(true);

            const me = await getCurrentUser().catch(() => null);

            if (!me) {
                redirectToSignup(getAuthReturnUrl());
                return;
            }

            const result = await claimPreviewSession(previewSessionId);
            const claimedProjectId = pid(result);

            if (!claimedProjectId) {
                toast.error("Could not save preview.");
                return;
            }

            setProjectId(claimedProjectId);
            setStatus("Saved to CRM");
            localStorage.removeItem(K);
            toast.success("Saved to CRM.");
        } catch (error) {
            console.error(error);
            toast.error("Could not save preview.");
        } finally {
            setSaving(false);
        }
    }

    async function publish() {
        if (!projectId) {
            toast.error("Save to CRM first.");
            return;
        }

        try {
            setPublishing(true);
            await publishProject(projectId);
            toast.success("Publish queued successfully.");
        } catch (error) {
            console.error(error);
            toast.error("Could not publish website.");
        } finally {
            setPublishing(false);
        }
    }

    return (
        <main className="gpv4-page">
            <div className="gpv4-bg">
                <div className="gpv4-grid" />
            </div>

            <header className="gpv4-header">
                <button className="gpv4-back" onClick={() => nav("/")}>
                    <ArrowLeft size={18} />
                    Shriansh Nexus
                </button>

                <div className="gpv4-header-pill">
                    <Sparkles size={15} />
                    Free preview mode
                </div>

                <div className="gpv4-header-actions">
                    <button onClick={() => setSupportOpen(true)}>Need Help?</button>
                    <button onClick={() => redirectToLogin(getAuthReturnUrl())}>Login</button>
                    <button className="primary" onClick={() => redirectToSignup(getAuthReturnUrl())}>Sign up</button>
                </div>
            </header>

            <section className="gpv4-shell">
                <aside className="gpv4-left">
                    <div className="gpv4-assistant-badge">
                        <Sparkles size={15} />
                        AI Generator Assistant
                    </div>

                    <h1>Describe your business. Watch your website come alive.</h1>

                    <p className="gpv4-subtitle">
                        Nexus turns your idea into a website, CRM modules, lead flow and deploy-ready preview.
                    </p>

                    <div className="gpv4-chat-card">
                        <div className="gpv4-ai-row">
                            <div className="gpv4-ai-icon"><Sparkles size={17} /></div>
                            <div>
                                <strong>Tell me what you are building.</strong>
                                <span>I will prepare a premium preview and CRM-ready structure.</span>
                            </div>
                        </div>

                        <label>
                            Business description <b>*</b>
                            <textarea
                                value={form.description}
                                onChange={(event) => update("description", event.target.value)}
                                placeholder="e.g. Premium real estate portal for buy/rent listings, agents, enquiries and city pages..."
                            />
                        </label>
                    </div>

                    <div className="gpv4-form-card">
                        <div className="gpv4-form-title">
                            <Rocket size={16} />
                            Website brief
                        </div>

                        <div className="gpv4-form-grid">
                            <label>
                                Business name <b>*</b>
                                <input value={form.businessName} onChange={(event) => update("businessName", event.target.value)} placeholder="Urban Luxe Properties" />
                            </label>

                            <label>
                                Industry <b>*</b>
                                <select value={form.industry} onChange={(event) => update("industry", event.target.value)}>
                                    <option value="">Choose industry</option>
                                    {industries.map((item) => <option key={item} value={item}>{item}</option>)}
                                </select>
                            </label>

                            <label>
                                Goal <b>*</b>
                                <select value={form.goalOfWebsite} onChange={(event) => update("goalOfWebsite", event.target.value)}>
                                    <option value="">Choose goal</option>
                                    {goals.map((item) => <option key={item} value={item}>{item}</option>)}
                                </select>
                            </label>

                            <label>
                                Brand style
                                <select value={form.brandStyle} onChange={(event) => update("brandStyle", event.target.value)}>
                                    {styles.map((item) => <option key={item} value={item}>{item}</option>)}
                                </select>
                            </label>

                            <label>
                                Location
                                <input value={form.location} onChange={(event) => update("location", event.target.value)} placeholder="Ahmedabad, Gujarat" />
                            </label>

                            <label>
                                Audience
                                <input value={form.primaryAudience} onChange={(event) => update("primaryAudience", event.target.value)} placeholder="Families, startups, buyers..." />
                            </label>

                            <label className="wide">
                                Services / products
                                <textarea value={form.offerings} onChange={(event) => update("offerings", event.target.value)} placeholder="List services, products, programs, property types or treatments..." />
                            </label>
                        </div>

                        {errors.length > 0 && (
                            <div className="gpv4-validation">
                                {errors.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        )}

                        <div className="gpv4-generate-actions">
                            <button className="primary" disabled={errors.length > 0 || screen === "generating"} onClick={generate}>
                                {screen === "generating" ? <><Loader2 className="spin" size={17} />Generating...</> : <><Wand2 size={17} />Create My AI Website</>}
                            </button>

                            <button className="ghost" onClick={() => {
                                setScreen("setup");
                                setPreviewSessionId("");
                                setProjectId("");
                                setStatus("");
                                setRevealReady(false);
                            }}>
                                <RefreshCw size={16} />
                                Start Again
                            </button>
                        </div>
                    </div>
                </aside>

                <section className="gpv4-preview-panel">
                    <div className="gpv4-preview-top">
                        <div>
                            <span>Live website preview</span>
                            <strong>{form.businessName || "Your AI website"}</strong>
                        </div>

                        <div className="gpv4-variant-toggle">
                            <button className={variant === "A" ? "active" : ""} onClick={() => setVariant("A")}>A</button>
                            <button className={variant === "B" ? "active" : ""} onClick={() => setVariant("B")}>B</button>
                            <button onClick={() => setRefreshKey(Date.now())}><RefreshCw size={15} /></button>
                        </div>
                    </div>

                    <div className={`gpv4-preview-frame ${revealReady ? "reveal" : ""}`}>
                        {screen === "preview" && doc ? (
                            <iframe title="Website preview" src={doc} />
                        ) : revealReady ? (
                            <div className="gpv4-ready-reveal">
                                <Sparkles size={42} />
                                <h2>Preview ready</h2>
                                <p>Opening your generated website...</p>
                            </div>
                        ) : (
                            <DynamicIndustryPreview form={form} screen={screen} stepIndex={stepIndex} />
                        )}
                    </div>

                    <div className="gpv4-status-card">
                        <span>Status</span>
                        <strong>{status || "Not generated yet"}</strong>
                        <p>
                            {projectId
                                ? "Saved to CRM. You can publish or open the project command center."
                                : previewSessionId
                                    ? "Preview session ready. Save it to CRM to own and publish this website."
                                    : "Choose an industry and Nexus will show the matching blueprint preview before generation."}
                        </p>
                    </div>

                    <div className="gpv4-preview-actions">
                        {!projectId && previewSessionId && (
                            <button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save to CRM"}</button>
                        )}

                        {projectId && (
                            <>
                                <button onClick={publish} disabled={publishing}>{publishing ? "Publishing..." : "Publish Website"}</button>
                                <button className="ghost" onClick={() => (window.location.href = `${ADMIN_BASE_URL}/crm/projects/${projectId}`)}>
                                    <ExternalLink size={16} />
                                    Open CRM
                                </button>
                            </>
                        )}
                    </div>

                    <div className="gpv4-trust-strip">
                        <span><CheckCircle2 size={15} />CRM modules planned</span>
                        <span><CheckCircle2 size={15} />Blueprint-safe V2</span>
                        <span><CheckCircle2 size={15} />Save → Sync → Redeploy → Live</span>
                    </div>
                </section>
            </section>

            <PublicSupportModal
                open={supportOpen}
                onClose={() => setSupportOpen(false)}
                previewSessionId={previewSessionId}
                businessName={form.businessName}
                industry={form.industry}
            />
        </main>
    );
}